/**
 * Consent-aware analytics loader.
 *
 * Three categories:
 *   - necessary  → always on (required)
 *   - analytics  → PostHog
 *   - marketing  → Meta Pixel (selain) + Meta Conversions API (palvelin)
 *
 * Both Pixel and CAPI are OFF by default and require explicit marketing
 * consent. Voiceflow is loaded on chat open (see lib/voiceflow.js), not here.
 */

const CONSENT_KEY = 'agencive-consent-v1';
const META_PIXEL_ID = '2200724270695118';
const POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_HOST = 'https://us.i.posthog.com';

export const openConsentPreferences = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('agencive:open-consent'));
  }
};

const listeners = new Set();

export const getConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Back-compat: older records only had `analytics`. Do NOT auto-upgrade
    // that flag into `marketing` — marketing is stricter and requires an
    // explicit opt-in.
    return {
      necessary: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      ts: parsed.ts,
    };
  } catch {
    return null;
  }
};

export const setConsent = (value) => {
  const normalised = {
    necessary: true,
    analytics: !!value.analytics,
    marketing: !!value.marketing,
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...normalised, ts: Date.now() }));
  } catch {
    /* ignore */
  }
  if (normalised.analytics) loadPostHog();
  if (normalised.marketing) loadMetaPixel();
  listeners.forEach((fn) => {
    try { fn(normalised); } catch { /* ignore */ }
  });
};

export const onConsentChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

let pixelLoaded = false;
export const loadMetaPixel = () => {
  if (pixelLoaded || typeof window === 'undefined') return;
  pixelLoaded = true;
  /* eslint-disable */
  !(function(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
};

let posthogLoaded = false;
export const loadPostHog = () => {
  if (posthogLoaded || typeof window === 'undefined') return;
  posthogLoaded = true;
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r; e.__SV || ((window.posthog = e), (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split('.');
          2 == o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); });
        }
        ((p = t.createElement('script')).type = 'text/javascript'),
          (p.crossOrigin = 'anonymous'), (p.async = !0),
          (p.src = s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js'),
          (r = t.getElementsByTagName('script')[0]).parentNode.insertBefore(p, r);
        var u = e; for (void 0 !== a ? (u = e[a] = []) : (a = 'posthog'),
          u.people = u.people || [], u.toString = function (t) {
            var e = 'posthog'; return ('posthog' !== a && (e += '.' + a), t || (e += ' (stub)'), e);
          }, u.people.toString = function () { return u.toString(1) + '.people (stub)'; },
          o = 'init capture identify reset alias register register_once opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing set_config get_distinct_id get_property get_session_id get_session_replay_url'.split(' '),
          n = 0; n < o.length; n++) g(u, o[n]); e._i.push([i, s, a]);
      }), (e.__SV = 1));
  })(document, window.posthog || []);
  /* eslint-enable */
  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: true,
    session_recording: { recordCrossOriginIframes: true, capturePerformance: false },
  });
};

/**
 * Initialise consent on app boot. Only load categories that the user has
 * explicitly opted into previously. Also set robots meta noindex on non-
 * production hostnames.
 */
export const initConsent = () => {
  if (typeof window === 'undefined') return;

  try {
    const host = window.location.hostname || '';
    const isProd = host === 'agencive.fi' || host === 'www.agencive.fi';
    if (!isProd) {
      let robots = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow');
    }
  } catch {
    /* ignore */
  }

  const existing = getConsent();
  if (!existing) return;
  if (existing.analytics) loadPostHog();
  if (existing.marketing) loadMetaPixel();
};
